import { LinearProgress } from '@mui/joy';

import { useEffect } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { useSetPageTitle } from '@/hooks/useSetPageTitle';
import { useHostProject, useUpdateOnboardingState } from '@/hooks/useSwr';

import { useFallbackTenant } from '../NoMatch/useFallbackTenant';
import { LangGraphContext } from './src/api';
import { useGraphThread } from './src/api/useGraphSwr';
import {
  GraphPlaygroundPaneAndTrigger,
  PlaygroundPane,
} from './src/components/debug/trace/GraphPlaygroundPane';
import { GraphTraceLogRowSmith } from './src/components/debug/trace/GraphTraceLogRow.smith';
import {
  InputHistoryContext,
  useInputHistory,
} from './src/components/debug/trace/hooks/useInputHistory';
import { ExperimentModalAndButton } from './src/components/site/ExperimentModal';
import { BackButton } from './src/components/site/GraphTopBar';
import { GraphInitializeError } from './src/components/site/error/GraphInitializeError';
import {
  StateContext,
  getStoredBreakpoints,
  useStateCtx,
} from './src/control/state';
import { useAssistantToInitialize } from './src/hooks/assistants/useAssistantToInitialize';
import { useInitializeAssistantState } from './src/hooks/assistants/useInitializeAssistantState';
import { useIntializeStudioClient } from './src/hooks/useIntializeStudioClient';
import { useStudioAnalytics } from './src/hooks/useStudioAnalytics';
import {
  StudioFeatureFlagsContext,
  useStudioFeatureFlags,
} from './src/hooks/useStudioFeatureFlagsContext';
import { StudioModeContext, useStudioMode } from './src/hooks/useStudioMode';
import {
  StudioRenderModeContext,
  useStudioRenderMode,
} from './src/hooks/useStudioRenderMode';
import { LangSmithAwareContext } from './src/langsmith';

function DeploymentProvider(props: { hostProjectId: string }) {
  const project = useHostProject(props.hostProjectId);
  if (project.isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }
  if (project.error) {
    return <ExpandableErrorAlert error={project.error} />;
  }

  return <Outlet context={{ standalone: false }} />;
}

function LocalGraphProvider() {
  return <Outlet context={{ standalone: false }} />;
}

export function GraphLayout() {
  const navigate = useNavigate();
  useSetPageTitle({
    isActive: true,
    name: 'LangGraph Studio',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const hostProjectId = searchParams.get('hostProjectId');
  const rawOrganizationId = searchParams.get('organizationId');

  const { tenant: targetTenant } = useFallbackTenant({
    skipCreate: true,
  });

  useEffect(() => {
    if (targetTenant && !rawOrganizationId) {
      // re-direct to same page but add query param for organizationId = lastTenantId. keep existing query params
      const currentSearchParams = new URLSearchParams(window.location.search);
      currentSearchParams.set('organizationId', targetTenant.id);
      navigate(`?${currentSearchParams.toString()}`, { replace: true });
    }
  }, [targetTenant, navigate, rawOrganizationId]);

  const { client, isLoadingClient } = useIntializeStudioClient({
    hostProjectId,
  });

  useStudioAnalytics();
  const { studioMode, setStudioMode } = useStudioMode(setSearchParams);
  const { renderMode, setRenderMode } = useStudioRenderMode(setSearchParams);

  const inputHistory = useInputHistory();

  const { trigger: updateOnboardingState } = useUpdateOnboardingState(
    'lgstudio_completed_at'
  );

  useEffect(() => {
    updateOnboardingState({});
  }, [updateOnboardingState]);

  if (isLoadingClient) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LinearProgress />
      </div>
    );
  }
  if (!client)
    return (
      <ExpandableErrorAlert
        error={new Error('Failed to connect to LangGraph Server')}
      />
    );

  return (
    <LangGraphContext.Provider value={client}>
      <StudioRenderModeContext.Provider value={{ renderMode, setRenderMode }}>
        <StudioModeContext.Provider value={{ studioMode, setStudioMode }}>
          {/* this context is used to supply langsmith aware components/information to the graph */}
          <LangSmithAwareContext.Provider
            value={
              rawOrganizationId
                ? {
                    TraceLogRow: GraphTraceLogRowSmith,
                    PlaygroundPaneAndTrigger: GraphPlaygroundPaneAndTrigger,
                    PlaygroundPane: PlaygroundPane,
                    ExperimentModalAndButton: ExperimentModalAndButton,
                  }
                : {
                    ExperimentModalAndButton: ExperimentModalAndButton,
                  }
            }
          >
            <InputHistoryContext.Provider value={inputHistory}>
              <GraphLayoutInner hostProjectId={hostProjectId} />
            </InputHistoryContext.Provider>
          </LangSmithAwareContext.Provider>
        </StudioModeContext.Provider>
      </StudioRenderModeContext.Provider>
    </LangGraphContext.Provider>
  );
}

function GraphLayoutInner({ hostProjectId }: { hostProjectId: string | null }) {
  const [searchParams] = useSearchParams();

  const { showGraphZoomControls } = useStudioFeatureFlags();

  const threadId = searchParams.get('threadId') ?? undefined;
  const thread = useGraphThread(threadId);
  const {
    assistant,
    intitializeError,
    initializeLoading,
    fetchedAssistant,
    onAssistantIdChange,
  } = useAssistantToInitialize({
    graphId: thread.data?.metadata?.graph_id,
  });

  useInitializeAssistantState({
    initAssistantId: assistant?.assistant_id,
    onAssistantIdChange,
  });

  const state = useStateCtx({
    initRunState: threadId ? { type: 'completed' } : { type: 'empty' },
  });

  useEffect(() => {
    if (assistant?.assistant_id) {
      const breakpoints = getStoredBreakpoints(assistant.assistant_id);
      state.setBeforeBreakpoints(breakpoints.beforeBreakpoints);
      state.setAfterBreakpoints(breakpoints.afterBreakpoints);
    }
  }, [assistant?.assistant_id]);

  if (intitializeError) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-2">
          <BackButton hostProjectId={hostProjectId} />
          <h2 className="text-xl font-medium tracking-tighter">
            Failed to initialize LangGraph Studio
          </h2>
          <p className="text-sm text-secondary">
            Please verify if the API server is running or accessible from the
            browser.
          </p>
        </div>
        <GraphInitializeError error={intitializeError} />
      </div>
    );
  }

  if (initializeLoading && !fetchedAssistant) {
    return (
      <div className="flex w-full items-start justify-center">
        <LinearProgress />
      </div>
    );
  }

  return (
    <StudioFeatureFlagsContext.Provider
      value={{ enableMarkdownInStudio: true, showGraphZoomControls }}
    >
      <StateContext.Provider value={state}>
        <div className="flex h-screen flex-col">
          <div className="relative h-full w-full">
            <div className="absolute inset-0 overflow-y-auto overflow-x-hidden">
              {hostProjectId ? (
                <DeploymentProvider hostProjectId={hostProjectId} />
              ) : (
                <LocalGraphProvider />
              )}
            </div>
          </div>
        </div>
      </StateContext.Provider>
    </StudioFeatureFlagsContext.Provider>
  );
}
